import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ["wrapper", "content", "marqueeItems"];

  connectedCallback() {
    super.connectedCallback();

    const numberOfCopies = this.#calculateNumberOfCopies();

    this.#syncRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));
    this.#setObservedInlineSizes();
    this.#resizeObserver.observe(this);

    const sourceItem = this.refs.content.firstElementChild;
    if (sourceItem) this.#resizeObserver.observe(sourceItem);
    if (!this.hasAttribute("data-disable-hover-slowdown")) {
      this.addEventListener("pointerenter", this.#slowDown);
      this.addEventListener("pointerleave", this.#speedUp);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
    if (!this.hasAttribute("data-disable-hover-slowdown")) {
      this.removeEventListener("pointerenter", this.#slowDown);
      this.removeEventListener("pointerleave", this.#speedUp);
    }
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty("--marquee-speed", `${value}s`);
  }

  #calculateSpeed(numberOfCopies = this.#calculateNumberOfCopies()) {
    const speedFactor = Number(this.getAttribute("data-speed-factor"));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;
    return speed;
  }

  #lastContainerInlineSize = 0;

  #lastSourceInlineSize = 0;

  #resizeObserver = new ResizeObserver(() => {
    if (!this.isConnected || !this.#haveInlineSizesChanged()) return;

    const numberOfCopies = this.#calculateNumberOfCopies();

    this.#syncRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));
  });

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = this.#cloneWithoutRefs(this.refs.content);

    clone.setAttribute("aria-hidden", "true");

    this.refs.wrapper.appendChild(clone);
  }

  #syncRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const sourceItem = content.firstElementChild;

    if (!sourceItem) return;

    for (const item of Array.from(content.children).slice(1)) {
      this.#removeRefs(item);
    }

    const targetNumberOfCopies = Math.max(1, numberOfCopies);

    while (content.children.length < targetNumberOfCopies) {
      content.appendChild(this.#cloneWithoutRefs(sourceItem));
    }

    while (content.children.length > targetNumberOfCopies) {
      content.lastElementChild?.remove();
    }
  }

  #cloneWithoutRefs(element) {
    const clone = /** @type {HTMLElement} */ (element.cloneNode(true));

    this.#removeRefs(clone);

    return clone;
  }

  #removeRefs(element) {
    element.removeAttribute("ref");

    for (const descendant of element.querySelectorAll("[ref]")) {
      descendant.removeAttribute("ref");
    }
  }

  #setObservedInlineSizes() {
    const { content } = this.refs;

    this.#lastContainerInlineSize = this.offsetWidth;
    this.#lastSourceInlineSize = content.firstElementChild?.offsetWidth ?? 0;
  }

  #haveInlineSizesChanged() {
    const { content } = this.refs;
    const containerInlineSize = this.offsetWidth;
    const sourceInlineSize = content.firstElementChild?.offsetWidth ?? 0;

    if (
      containerInlineSize === this.#lastContainerInlineSize &&
      sourceInlineSize === this.#lastSourceInlineSize
    ) {
      return false;
    }

    this.#lastContainerInlineSize = containerInlineSize;
    this.#lastSourceInlineSize = sourceInlineSize;

    return true;
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild?.offsetWidth ?? 1;

    return marqueeRepeatedItemWidth === 0
      ? 1
      : Math.max(1, Math.ceil(marqueeWidth / marqueeRepeatedItemWidth));
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) => t * t * (3 - 2 * t),
  onComplete,
}) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === "function") {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get("marquee-component")) {
  customElements.define("marquee-component", MarqueeComponent);
}
